import { ArrowLeft, ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import { Navigate, useParams } from "react-router";

import { ButtonLink } from "@/components/button-link";
import { EmptyState, ErrorState, LoadingRows, Spinner } from "@/components/data-state";
import {
  CopyableId,
  DetailsSection,
  FailureNotice,
  Field,
  NeighbourLink,
  ResultTiles,
  TrajectorySection,
} from "@/components/run-detail";
import { RunOutcome } from "@/components/run-outcome";
import { Badge } from "@/components/ui/badge";
import { useAsync, usePolling } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import { isPending, isSettled } from "@/lib/api";
import type { Submission } from "@/lib/api";
import { publicApi, studentApi } from "@/lib/api";
import { formatRelative, formatTimestamp } from "@/lib/format";
import { bestRun } from "@/lib/scoring";

/**
 * One uploaded recording: what became of it, and the path it took.
 *
 * This is the client-simulated challenge's page. A server-simulated run has a session
 * behind it, and the session -- not the submission -- is what the student did, so its
 * submission redirects to the attempt's page. Links by `job_id` from the leaderboard keep
 * working either way.
 */
export function SubmissionDetailRoute() {
  const { id = "", submissionId = "" } = useParams();
  const apiKey = useApiKey();

  // Keyed on the api key: an `enrolled` challenge is a 401 until the student signs
  // in, and the page has to refetch when they do rather than stay on the error.
  const challenge = useAsync(() => publicApi.challenge(id), [id, apiKey]);

  // Poll while the background evaluation is unfinished. There are two such states
  // now, `queued` and `running`, where the old API had a single `pending` -- hence
  // isSettled rather than a literal comparison.
  const job = usePolling<Submission>(
    apiKey ? () => studentApi.submission(id, submissionId, apiKey) : null,
    (value) => isSettled(value.status),
    2000,
    [id, submissionId, apiKey],
  );

  // The student's other attempts at this challenge: they give this one its number,
  // its neighbours, and the "your best" it is measured against.
  const siblings = useAsync(
    apiKey ? () => studentApi.submissions(id, apiKey) : null,
    [id, apiKey, job.data?.status],
  );

  if (!apiKey) {
    return <EmptyState title="Sign in to view this submission" />;
  }
  if (job.error) {
    return <ErrorState error={job.error} onRetry={job.refetch} />;
  }
  if (!job.data || !challenge.data) {
    return <LoadingRows rows={3} />;
  }

  const sub = job.data;
  const ch = challenge.data;

  if (sub.session_id) {
    return <Navigate to={`/challenges/${id}/attempts/${sub.session_id}`} replace />;
  }

  // Oldest first, so "attempt 7" is stable as new ones arrive.
  const ordered = [...(siblings.data ?? [])].sort(
    (a, b) => a.submitted_at - b.submitted_at,
  );
  const index = ordered.findIndex((s) => s.job_id === submissionId);
  const prev = index > 0 ? ordered[index - 1] : null;
  const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;
  const best = bestRun(ordered);
  const isBest = best?.job_id === submissionId;
  const settled = isSettled(sub.status);

  return (
    <div className="space-y-6">
      {/* --- header --- */}
      <div className="space-y-3">
        <ButtonLink
          variant="ghost"
          size="sm"
          to={`/challenges/${id}`}
          className="-ml-2.5"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {ch.name}
        </ButtonLink>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {index >= 0 ? `Attempt ${index + 1}` : "Submission"}
                {ordered.length > 1 && index >= 0 ? (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    of {ordered.length}
                  </span>
                ) : null}
              </h1>
              <RunOutcome submission={sub} />
              {isBest && ordered.length > 1 ? (
                <Badge variant="default" className="gap-1">
                  <Trophy className="size-3" aria-hidden />
                  Your best
                </Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-sm">
              Submitted {formatTimestamp(sub.submitted_at)}
              <span className="text-muted-foreground/70">
                {" · "}
                {formatRelative(sub.submitted_at)}
              </span>
            </p>
          </div>

          {ordered.length > 1 ? (
            <div className="flex shrink-0 items-center gap-1">
              <NeighbourLink
                to={prev ? `/challenges/${id}/submissions/${prev.job_id}` : null}
                label="Previous attempt"
              >
                <ChevronLeft className="size-4" aria-hidden />
                <span className="hidden sm:inline">Previous</span>
              </NeighbourLink>
              <NeighbourLink
                to={next ? `/challenges/${id}/submissions/${next.job_id}` : null}
                label="Next attempt"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="size-4" aria-hidden />
              </NeighbourLink>
            </div>
          ) : null}
        </div>
      </div>

      {/* --- outcome --- */}
      {sub.status === "failed" ? (
        <FailureNotice sub={sub} />
      ) : isPending(sub.status) ? (
        <div className="bg-muted/40 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm">
          <Spinner className="size-4" />
          <span>
            {sub.status === "queued"
              ? "Queued — waiting for a judge to pick this run up."
              : "Evaluating — scoring the recording now."}
          </span>
          <span className="text-muted-foreground ml-auto text-xs">
            Refreshes automatically
          </span>
        </div>
      ) : (
        <ResultTiles
          challenge={ch}
          arrival={sub.arrival}
          spl={sub.spl}
          steps={sub.nav_steps}
          navTimeS={sub.nav_time_s}
          score={sub.score}
          best={
            best
              ? {
                  tier: best.goal_tier,
                  steps: best.nav_steps,
                  points: best.score?.points ?? 0,
                  at: best.submitted_at,
                  href: `/challenges/${id}/submissions/${best.job_id}`,
                }
              : null
          }
          isBest={isBest}
          attempts={ordered.length}
        />
      )}

      {/* The plot and the record. Each is a fixed width so nothing stretches to fill. */}
      <div className="grid gap-6 lg:grid-cols-[26rem_19rem]">
        <TrajectorySection
          challenge={ch}
          jobId={sub.job_id}
          // A failed run still has a recording; not while queued or running, when
          // there is nothing to draw yet.
          ready={settled}
          apiKey={apiKey}
          note={
            sub.status === "failed" && ch.show_trajectory
              ? "not scored; the path as recorded"
              : undefined
          }
        />

        <DetailsSection>
          <Field label="Job id">
            <CopyableId value={sub.job_id} />
          </Field>
          <Field label="Status">
            <span className="capitalize">{sub.status}</span>
          </Field>
          <Field label="Submitted">{formatTimestamp(sub.submitted_at)}</Field>
          <Field label="Account">
            <span className="truncate">{sub.email}</span>
          </Field>
        </DetailsSection>
      </div>
    </div>
  );
}
